/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Quick Link Form Page Component
 * Full-page create/edit interface for quick links
 * Follows customer portal settings pattern with fixed height and sticky buttons
 */

import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";

// PrimeNG imports
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from "primeng/accordion";
import { ConfirmationService, MessageService } from "primeng/api";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { DatePickerModule } from "primeng/datepicker";
import { DividerModule } from "primeng/divider";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectModule } from "primeng/select";
import { SkeletonModule } from "primeng/skeleton";
import { TabsModule } from "primeng/tabs";
import { TagModule } from "primeng/tag";
import { TextareaModule } from "primeng/textarea";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Our imports
import { Site } from "../../../../core/models/site.interface";
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import {
  CreateQuickLinkRequest,
  UpdateQuickLinkRequest,
} from "../../interfaces/quick-link-api.interface";
import {
  ACTION_TYPE_OPTIONS,
  IQuickLinkForm,
  REDIRECT_TYPE_OPTIONS,
} from "../../interfaces/quick-link-form.interface";
import {
  QuickLinkListItem,
  QuickLinkStatus,
  RedirectType,
  UsageLimitType,
} from "../../interfaces/quick-link-list-item.interface";
import { QuickLinksService } from "../../services/quick-links.service";
import {
  parseEndOfDayUtcToDate,
  parseUtmParams,
  stringifyUtmParams,
  toEndOfDayUtcString,
} from "../../utils/enum-display.helpers";

@Component({
  selector: "app-quick-link-form-page",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TabsModule,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    TagModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    InputNumberModule,
    RadioButtonModule,
    CheckboxModule,
    DatePickerModule,
    DividerModule,
    ToastModule,
    SkeletonModule,
    TooltipModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  templateUrl: "./quick-link-form-page.component.html",
  styleUrl: "./quick-link-form-page.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickLinkFormPageComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode (web component / iframe)
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputQuickLinkId?: number;
  @Input() embeddedMode: boolean = false;

  // Page state
  readonly mode = signal<"create" | "edit">("create");
  readonly siteId = signal<number>(0);
  readonly site = signal<Site | null>(null);
  readonly quickLinkId = signal<number | null>(null);
  readonly quickLink = signal<QuickLinkListItem | null>(null);

  // Form
  quickLinkForm!: FormGroup<IQuickLinkForm>;

  // Loading state
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly verifying = signal(false);

  // Slug verification state
  readonly slugVerificationResult = signal<{
    verified: boolean;
    isAvailable: boolean;
    message: string;
  } | null>(null);

  // Responsive state
  readonly isMobile = signal(false);
  private resizeObserver?: ResizeObserver;

  // Subscription management
  private readonly destroy$ = new Subject<void>();

  // Progressive disclosure state
  readonly showUtmParams = signal(false);

  // Form change tracker signal (increments on every form change to trigger computed updates)
  private readonly formChangeTracker = signal(0);

  // Live preview scheduled order ID (for example URL)
  readonly previewScheduledOrderId = signal<string>("12345");

  // Form options
  readonly actionTypeOptions = ACTION_TYPE_OPTIONS;
  readonly redirectTypeOptions = REDIRECT_TYPE_OPTIONS;
  readonly usageLimitTypeOptions: Array<{
    value: UsageLimitType;
    label: string;
    description: string;
  }> = [
    // COMMENTED OUT: Single Use API not ready yet
    // {
    //   value: 0,
    //   label: "Single Use",
    //   description: "Generate unique tokens per customer",
    // },
    {
      value: 1,
      label: "Limited Use",
      description: "Set a maximum number of uses",
    },
    { value: 2, label: "Unlimited", description: "No usage limit" },
  ];

  // Validation state
  readonly today = new Date();

  // Tab state (desktop)
  activeTabIndex = 0;

  // Accordion state (mobile) - PrimeNG v18+ uses string values
  activeAccordionIndex: string | string[] = "0";

  // Section validation computed signals
  readonly isBasicInfoValid = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    if (!this.quickLinkForm) return false;
    const nameValid = this.quickLinkForm.controls.name.valid;
    // In edit mode, slug is disabled so consider it valid
    const slugValid =
      this.quickLinkForm.controls.slug.disabled ||
      this.quickLinkForm.controls.slug.valid;
    const actionTypeValid = this.quickLinkForm.controls.actionType.valid;
    return nameValid && slugValid && actionTypeValid;
  });

  readonly isConfigValid = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    if (!this.quickLinkForm) return false;
    const usageLimitTypeValid =
      this.quickLinkForm.controls.usageLimitType.valid;
    const maxUsesValid = this.isLimitedUse()
      ? this.quickLinkForm.controls.maxUses.valid
      : true;
    return usageLimitTypeValid && maxUsesValid;
  });

  readonly isRedirectValid = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    if (!this.quickLinkForm) return false;
    const redirectTypeValid = this.quickLinkForm.controls.redirectType.valid;
    const redirectUrlValid = this.isCustomUrl()
      ? this.quickLinkForm.controls.redirectUrl.valid
      : true;
    return redirectTypeValid && redirectUrlValid;
  });

  // Check if slug is verified and available (only for create mode)
  readonly isSlugVerifiedAndAvailable = computed(() => {
    if (this.mode() === "edit") return true; // In edit mode, slug is disabled
    const result = this.slugVerificationResult();
    return result !== null && result.verified && result.isAvailable;
  });

  constructor(
    private readonly elementRef: ElementRef,
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly quickLinksService: QuickLinksService,
    private readonly qpilotService: QPilotService,
    private readonly sessionService: SessionService,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    // Detect embedded mode from HTML attributes (for web components)
    this.detectEmbeddedModeFromAttributes();

    this.initializeFromRoute();
    this.initializeForm();
    this.setupConditionalValidators();
    this.setupFormChangeTracking();
    this.setupResponsiveDetection();
  }

  /**
   * Detect embedded mode from HTML attributes
   * Required for web component mode where @Input bindings don't work automatically
   */
  private detectEmbeddedModeFromAttributes(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check embedded-mode attribute
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.inputSiteId = parseInt(siteIdString, 10);
    }

    // Get quickLinkId from input/attribute (for edit mode)
    const quickLinkIdString =
      this.inputQuickLinkId?.toString() ??
      element.getAttribute("input-quick-link-id") ??
      element.getAttribute("inputQuickLinkId");

    if (quickLinkIdString) {
      this.inputQuickLinkId = parseInt(quickLinkIdString, 10);
    }

    // Get token from attribute and convert to QPilotAccessToken
    // Angular Elements may bind the attribute as a raw string, so we check for proper structure
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  getAccessToken(): QPilotAccessToken | undefined {
    // In embedded mode, use inputToken
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    // In standard mode, use session service (convert null to undefined)
    return this.sessionService.accessToken ?? undefined;
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup responsive breakpoint detection
   */
  private setupResponsiveDetection(): void {
    // Initial check
    this.checkScreenSize();

    // Watch for resize
    this.resizeObserver = new ResizeObserver(() => {
      this.checkScreenSize();
    });

    this.resizeObserver.observe(document.body);
  }

  /**
   * Check screen size and update isMobile signal
   */
  private checkScreenSize(): void {
    this.isMobile.set(window.innerWidth <= 768);
  }

  /**
   * Initialize component from route parameters
   */
  private initializeFromRoute(): void {
    if (this.embeddedMode) {
      // Embedded mode: use input properties
      if (this.inputSiteId) {
        this.siteId.set(this.inputSiteId);
        this.loadSite();
      } else {
        console.error("❌ No inputSiteId provided in embedded mode!");
      }

      // Check for quickLinkId in embedded mode (edit)
      if (this.inputQuickLinkId) {
        this.mode.set("edit");
        this.quickLinkId.set(this.inputQuickLinkId);
        this.loadQuickLinkForEdit(this.inputQuickLinkId);
      } else {
        this.mode.set("create");
      }
    } else {
      // Standard mode: get from route parameters
      const siteId =
        this.route.snapshot.paramMap.get("siteId") ||
        this.route.parent?.snapshot.paramMap.get("siteId");

      if (siteId) {
        this.siteId.set(parseInt(siteId, 10));
        // Load site data for UtcOffset
        this.loadSite();
      } else {
        console.error("❌ No siteId found in route parameters!");
      }

      // Get quickLinkId from route (edit mode)
      const quickLinkId = this.route.snapshot.paramMap.get("id");
      if (quickLinkId) {
        this.mode.set("edit");
        this.quickLinkId.set(parseInt(quickLinkId, 10));
        this.loadQuickLinkForEdit(parseInt(quickLinkId, 10));
      } else {
        this.mode.set("create");
      }
    }
  }

  /**
   * Load site data to get UtcOffset for date calculations
   */
  private loadSite(): void {
    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    this.qpilotService
      .getSite(this.siteId(), accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site) => {
          // Cast to Site from site.interface.ts which has UtcOffset
          // The API returns full site data, but qpilot.service.ts has minimal type
          const fullSite = site as unknown as Site;
          this.site.set(fullSite);

          // If we're in edit mode and have quick link data loaded,
          // re-parse the expiredUtc with the correct site offset
          const quickLink = this.quickLink();
          if (this.mode() === "edit" && quickLink?.expiredUtc) {
            this.updateExpiredUtcFromQuickLink(quickLink.expiredUtc);
          }
        },
        error: (err) => {
          console.error("❌ Failed to load site:", err);
        },
      });
  }

  /**
   * Update the form's expiredUtc control from a UTC string using site's timezone
   */
  private updateExpiredUtcFromQuickLink(expiredUtc: string | null): void {
    if (!expiredUtc) {
      this.quickLinkForm.controls.expiredUtc.setValue(null);
      return;
    }

    const siteUtcOffset = this.site()?.UtcOffset;
    const expiredDate = parseEndOfDayUtcToDate(expiredUtc, siteUtcOffset);

    this.quickLinkForm.controls.expiredUtc.setValue(expiredDate);
  }

  /**
   * Load quick link data for edit mode
   */
  private loadQuickLinkForEdit(id: number): void {
    this.loading.set(true);
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "You must be logged in to edit quick links",
        life: 5000,
      });
      this.navigateBack();
      return;
    }

    this.quickLinksService
      .getQuickLink(this.siteId(), id, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (quickLink: QuickLinkListItem) => {
          this.quickLink.set(quickLink);
          this.populateFormForEdit(quickLink);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.navigateBack();
        },
      });
  }

  /**
   * Initialize the form with default values
   */
  private initializeForm(): void {
    this.quickLinkForm = this.fb.group<IQuickLinkForm>({
      name: this.fb.nonNullable.control("", [
        Validators.required,
        Validators.maxLength(255),
      ]),
      slug: this.fb.nonNullable.control("", [
        Validators.required,
        Validators.pattern(/^[a-z0-9]+(-[a-z0-9]+)*$/),
        Validators.maxLength(100),
      ]),
      actionType: this.fb.nonNullable.control(0, [Validators.required]),
      usageLimitType: this.fb.nonNullable.control(2, [Validators.required]), // Default to Unlimited (2)
      maxUses: this.fb.control(null as number | null),
      requiresLogin: this.fb.nonNullable.control(false),
      requiresConfirmation: this.fb.nonNullable.control(false),
      expiredUtc: this.fb.control(null as Date | null),
      redirectType: this.fb.nonNullable.control(0 as RedirectType, [
        Validators.required,
      ]),
      redirectMessage: this.fb.control(null as string | null),
      redirectUrl: this.fb.control(null as string | null),
      utmSource: this.fb.control(null as string | null),
      utmMedium: this.fb.control(null as string | null),
      utmCampaign: this.fb.control(null as string | null),
      utmContent: this.fb.control(null as string | null),
    });

    // Auto-generate slug from name (create mode only)
    this.quickLinkForm.controls.name.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        if (this.mode() === "create" && name) {
          const slug = this.generateSlug(name);
          this.quickLinkForm.controls.slug.setValue(slug, { emitEvent: true });
        }
      });

    // Auto-verify slug with debounce (create mode only)
    this.quickLinkForm.controls.slug.valueChanges
      .pipe(debounceTime(800), takeUntil(this.destroy$))
      .subscribe((slug) => {
        if (this.mode() === "create" && slug && slug.trim() !== "") {
          // Only auto-verify if slug matches the pattern
          const slugPattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
          if (slugPattern.test(slug)) {
            this.autoVerifySlug(slug);
          }
        }
      });
  }

  /**
   * Setup form change tracking for reactive computed properties
   */
  private setupFormChangeTracking(): void {
    // Subscribe to form value changes and increment tracker
    this.quickLinkForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formChangeTracker.update((count) => count + 1);
      });
  }

  /**
   * Setup conditional validators based on form values
   */
  private setupConditionalValidators(): void {
    // Apply validators based on initial form state
    this.applyConditionalValidators();

    // When usageLimitType changes, update maxUses value and validators
    // SingleUse (0): maxUses = 1
    // LimitedUse (1): maxUses = user input (default 100)
    // Unlimited (2): maxUses = null
    this.quickLinkForm.controls.usageLimitType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((usageLimitType) => {
        const maxUsesControl = this.quickLinkForm.controls.maxUses;

        if (usageLimitType === 0) {
          // SingleUse: maxUses must be 1
          maxUsesControl.setValue(1);
          maxUsesControl.clearValidators();
        } else if (usageLimitType === 1) {
          // Limited Use: maxUses is required, default to 100 if null
          const minValue = this.minMaxUses();
          if (maxUsesControl.value === null || maxUsesControl.value === 1) {
            maxUsesControl.setValue(Math.max(100, minValue));
          }
          maxUsesControl.setValidators([
            Validators.required,
            Validators.min(minValue),
            Validators.max(10000),
          ]);
        } else if (usageLimitType === 2) {
          // Unlimited: maxUses must be null
          maxUsesControl.setValue(null);
          maxUsesControl.clearValidators();
        }

        maxUsesControl.updateValueAndValidity();
      });

    // When redirectType changes, clear mutually exclusive fields and update validators
    // Thank You Page (0): use redirectMessage, clear redirectUrl
    // V2 Portal (1): use neither, clear both
    // Custom URL (2): use redirectUrl, clear redirectMessage
    this.quickLinkForm.controls.redirectType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((redirectType) => {
        const redirectUrlControl = this.quickLinkForm.controls.redirectUrl;
        const redirectMessageControl =
          this.quickLinkForm.controls.redirectMessage;

        if (redirectType === 0) {
          // Thank You Page: clear custom URL
          redirectUrlControl.setValue(null);
          redirectUrlControl.clearValidators();
        } else if (redirectType === 1) {
          // V2 Portal: clear both fields
          redirectMessageControl.setValue(null);
          redirectUrlControl.setValue(null);
          redirectUrlControl.clearValidators();
        } else if (redirectType === 2) {
          // Custom URL: clear thank you page message, set URL validators
          redirectMessageControl.setValue(null);
          redirectUrlControl.setValidators([
            Validators.required,
            Validators.pattern(/^https?:\/\/.+/),
          ]);
        }

        redirectUrlControl.updateValueAndValidity();
      });

    // When actionType changes, update requiresLogin based on action type
    // Process Now (2): requiresLogin must be true and is disabled
    this.quickLinkForm.controls.actionType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((actionType) => {
        const requiresLoginControl = this.quickLinkForm.controls.requiresLogin;
        const requiresConfirmationControl =
          this.quickLinkForm.controls.requiresConfirmation;

        if (actionType === 2) {
          // Process Now: set requiresLogin to true and disable
          requiresLoginControl.setValue(true);
          requiresLoginControl.disable();

          // Process Now: set requiresConfirmation to true and disable
          requiresConfirmationControl.setValue(true);
          requiresConfirmationControl.disable();
        } else {
          // Other action types: enable
          requiresLoginControl.enable();
          requiresLoginControl.clearValidators();

          // Other action types: enable
          requiresConfirmationControl.enable();
          requiresConfirmationControl.clearValidators();
        }

        requiresLoginControl.updateValueAndValidity();
        requiresConfirmationControl.updateValueAndValidity();
      });
  }

  /**
   * Apply conditional validators based on current form state
   * This is called on initial load and after loading edit data
   */
  private applyConditionalValidators(): void {
    // Apply maxUses validators based on current usageLimitType
    const usageLimitType = this.quickLinkForm.controls.usageLimitType.value;
    const maxUsesControl = this.quickLinkForm.controls.maxUses;

    if (usageLimitType === 1) {
      // Limited Use: maxUses is required
      const minValue = this.minMaxUses();
      maxUsesControl.setValidators([
        Validators.required,
        Validators.min(minValue),
        Validators.max(10000),
      ]);
    } else {
      // Unlimited or Single Use: no validators
      maxUsesControl.clearValidators();
    }
    maxUsesControl.updateValueAndValidity();

    // Apply redirectUrl validators based on current redirectType
    const redirectType = this.quickLinkForm.controls.redirectType.value;
    const redirectUrlControl = this.quickLinkForm.controls.redirectUrl;

    if (redirectType === 2) {
      // Custom URL: redirectUrl is required
      redirectUrlControl.setValidators([
        Validators.required,
        Validators.pattern(/^https?:\/\/.+/),
      ]);
    } else {
      // Thank You Page or V2 Portal: no validators
      redirectUrlControl.clearValidators();
    }
    redirectUrlControl.updateValueAndValidity();

    // Apply requiresLogin validators based on current actionType
    const actionType = this.quickLinkForm.controls.actionType.value;
    const requiresLoginControl = this.quickLinkForm.controls.requiresLogin;
    const requiresConfirmationControl =
      this.quickLinkForm.controls.requiresConfirmation;

    if (actionType === 2) {
      // Process Now: requiresLogin must be true, set value and disable
      requiresLoginControl.setValue(true);
      requiresLoginControl.disable();

      // Process Now: requiresConfirmation must be true, set value and disable
      requiresConfirmationControl.setValue(true);
      requiresConfirmationControl.disable();
    } else {
      // Other action types: enable and clear validators
      requiresLoginControl.enable();
      requiresLoginControl.clearValidators();

      // Other action types: enable and clear validators
      requiresConfirmationControl.enable();
      requiresConfirmationControl.clearValidators();
    }
    requiresLoginControl.updateValueAndValidity();
    requiresConfirmationControl.updateValueAndValidity();
  }

  /**
   * Populate form for edit mode
   */
  private populateFormForEdit(quickLink: QuickLinkListItem): void {
    // Parse UTC expiredUtc to Date using site's timezone offset
    // This converts the end-of-day UTC timestamp back to the original selected date
    const siteUtcOffset = this.site()?.UtcOffset;
    const expiredDate = parseEndOfDayUtcToDate(
      quickLink.expiredUtc,
      siteUtcOffset,
    );

    // Parse UTM params from JSON string
    const utmParams = parseUtmParams(quickLink.redirect?.utmParams || null);

    this.quickLinkForm.patchValue({
      name: quickLink.name,
      slug: quickLink.slug,
      actionType: quickLink.actionType,
      usageLimitType: quickLink.usageLimitType,
      maxUses: quickLink.maxUses,
      requiresLogin: quickLink.requiresLogin,
      requiresConfirmation: quickLink.requiresConfirmation,
      expiredUtc: expiredDate,
      redirectType: quickLink.redirect?.type || 0,
      redirectMessage: quickLink.redirect?.message || null,
      redirectUrl: quickLink.redirect?.customUrl || null,
      utmSource: utmParams?.utm_source || null,
      utmMedium: utmParams?.utm_medium || null,
      utmCampaign: utmParams?.utm_campaign || null,
      utmContent: utmParams?.utm_content || null,
    });

    // Disable slug field in edit mode (cannot change slug)
    this.quickLinkForm.controls.slug.disable();

    // Re-apply conditional validators based on loaded data
    this.applyConditionalValidators();
  }

  /**
   * Generate URL-friendly slug from name
   */
  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .substring(0, 100); // Limit length
  }

  /**
   * Check if limited use is selected
   */
  isLimitedUse(): boolean {
    return this.quickLinkForm.controls.usageLimitType.value === 1;
  }

  /**
   * Get current usage count (edit mode only)
   */
  readonly currentUsageCount = computed(() => {
    const quickLink = this.quickLink();
    return quickLink?.currentUsageCount ?? 0;
  });

  /**
   * Get minimum allowed value for maxUses (edit mode only)
   * Cannot set maxUses lower than current usage count
   */
  readonly minMaxUses = computed(() => {
    if (this.mode() === "create") return 1;
    return Math.max(1, this.currentUsageCount());
  });

  /**
   * Get available uses remaining (edit mode only, for limited use links)
   */
  readonly availableUses = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    if (!this.isLimitedUse()) return null;
    const maxUses = this.quickLinkForm?.controls.maxUses.value;
    const currentUsage = this.currentUsageCount();
    if (maxUses === null || maxUses === undefined) return null;
    return Math.max(0, maxUses - currentUsage);
  });

  /**
   * Get usage percentage (edit mode only, for limited use links)
   */
  readonly usagePercentage = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    if (!this.isLimitedUse()) return null;
    const maxUses = this.quickLinkForm?.controls.maxUses.value;
    const currentUsage = this.currentUsageCount();
    if (!maxUses || maxUses === 0) return null;
    return Math.round((currentUsage / maxUses) * 100);
  });

  /**
   * Check if usage limit has been reached (edit mode only)
   */
  readonly hasReachedLimit = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    if (!this.isLimitedUse()) return false;
    const maxUses = this.quickLinkForm?.controls.maxUses.value;
    const currentUsage = this.currentUsageCount();
    if (maxUses === null || maxUses === undefined) return false;
    return currentUsage >= maxUses;
  });

  /**
   * Check if approaching usage limit (>= 80%, edit mode only)
   */
  readonly isApproachingLimit = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    const percentage = this.usagePercentage();
    if (percentage === null) return false;
    return percentage >= 80 && percentage < 100;
  });

  /**
   * Check if custom URL is selected
   */
  isCustomUrl(): boolean {
    return this.quickLinkForm.controls.redirectType.value === 2;
  }

  /**
   * Check if thank you page is selected
   */
  isThankYouPage(): boolean {
    return this.quickLinkForm.controls.redirectType.value === 0;
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.quickLinkForm.invalid) {
      this.quickLinkForm.markAllAsTouched();
      this.messageService.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fix all validation errors before saving",
        life: 5000,
      });
      return;
    }

    this.saving.set(true);

    const formValues = this.quickLinkForm.getRawValue(); // getRawValue includes disabled fields

    // Build UTM params object
    const utmParams = {
      utm_source: formValues.utmSource || undefined,
      utm_medium: formValues.utmMedium || undefined,
      utm_campaign: formValues.utmCampaign || undefined,
      utm_content: formValues.utmContent || undefined,
    };

    // Build redirect config
    const redirect = {
      type: formValues.redirectType,
      message: formValues.redirectMessage || null,
      customUrl: formValues.redirectUrl || null,
      utmParams: stringifyUtmParams(utmParams),
    };

    if (this.mode() === "create") {
      this.createQuickLink(formValues, redirect);
    } else {
      this.updateQuickLink(formValues, redirect);
    }
  }

  /**
   * Create new quick link
   */
  private createQuickLink(formValues: any, redirect: any): void {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "You must be logged in to create quick links",
        life: 5000,
      });
      this.saving.set(false);
      return;
    }

    const request: CreateQuickLinkRequest = {
      name: formValues.name,
      slug: formValues.slug,
      actionType: formValues.actionType,
      usageLimitType: formValues.usageLimitType,
      maxUses: formValues.maxUses,
      requiresLogin: formValues.requiresLogin,
      requiresConfirmation: formValues.requiresConfirmation,
      expiredUtc: toEndOfDayUtcString(
        formValues.expiredUtc,
        this.site()?.UtcOffset,
      ),
      redirect,
    };

    this.quickLinksService
      .createQuickLink(this.siteId(), request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (createdQuickLink) => {
          this.messageService.add({
            severity: "success",
            summary: "Quick Link Created",
            detail: `"${createdQuickLink.name}" has been created successfully`,
            life: 5000,
          });
          this.navigateBack();
        },
        error: () => {
          this.saving.set(false);
        },
      });
  }

  /**
   * Update existing quick link
   */
  private updateQuickLink(formValues: any, redirect: any): void {
    const quickLinkId = this.quickLinkId();
    if (!quickLinkId) return;

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "You must be logged in to update quick links",
        life: 5000,
      });
      this.saving.set(false);
      return;
    }

    const request: UpdateQuickLinkRequest = {
      name: formValues.name,
      actionType: formValues.actionType,
      usageLimitType: formValues.usageLimitType,
      maxUses: formValues.maxUses,
      requiresLogin: formValues.requiresLogin,
      requiresConfirmation: formValues.requiresConfirmation,
      expiredUtc: toEndOfDayUtcString(
        formValues.expiredUtc,
        this.site()?.UtcOffset,
      ),
      redirect,
    };

    this.quickLinksService
      .updateQuickLink(this.siteId(), quickLinkId, request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedQuickLink) => {
          this.messageService.add({
            severity: "success",
            summary: "Quick Link Updated",
            detail: `"${updatedQuickLink.name}" has been updated successfully`,
            life: 5000,
          });
          // Update the local quick link data
          this.quickLink.set(updatedQuickLink);
          // Update the form's expiredUtc with the response value (converted to site's timezone)
          this.updateExpiredUtcFromQuickLink(updatedQuickLink.expiredUtc);
          // Reset saving state
          this.saving.set(false);
          // Stay on edit page after successful update
        },
        error: () => {
          this.saving.set(false);
        },
      });
  }

  /**
   * Handle cancel button
   */
  onCancel(): void {
    this.navigateBack();
  }

  /**
   * Navigate back to quick links list
   */
  private navigateBack(): void {
    if (this.embeddedMode) {
      // Emit event for parent application to handle
      const event = new CustomEvent("qmc-quick-link-saved", {
        detail: {
          component: "quick-link-form",
          action: "list",
          quickLinkId: this.quickLinkId(),
          siteId: this.siteId(),
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([
        "/widgets/dashboard",
        this.siteId(),
        "quick-actions",
      ]);
    }
  }

  /**
   * Toggle UTM parameters visibility
   */
  toggleUtmParams(): void {
    this.showUtmParams.set(!this.showUtmParams());
  }

  /**
   * Check if any UTM parameter has value
   */
  hasUtmParams(): boolean {
    const formValues = this.quickLinkForm.value;
    return !!(
      formValues.utmSource ||
      formValues.utmMedium ||
      formValues.utmCampaign ||
      formValues.utmContent
    );
  }

  /**
   * Auto-verify slug availability (silent, no toast notifications)
   * Called automatically after debounce when user types
   */
  private autoVerifySlug(slug: string): void {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return;
    }

    this.verifying.set(true);

    this.quickLinksService
      .slugifyText(this.siteId(), slug, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.slugVerificationResult.set({
            verified: true,
            isAvailable: response.isAvailable,
            message: response.message || "",
          });
          this.verifying.set(false);
        },
        error: () => {
          this.verifying.set(false);
          this.slugVerificationResult.set(null);
        },
      });
  }

  /**
   * Verify slug availability (manual, with toast notifications)
   * Called when user clicks the "Verify" button
   */
  verifySlug(): void {
    const slug = this.quickLinkForm.controls.slug.value;

    if (!slug || slug.trim() === "") {
      this.messageService.add({
        severity: "warn",
        summary: "Slug Required",
        detail: "Please enter a slug to verify",
        life: 3000,
      });
      return;
    }

    // Check if slug matches pattern before calling API
    const slugPattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    if (!slugPattern.test(slug)) {
      this.slugVerificationResult.set({
        verified: true,
        isAvailable: false,
        message:
          "Slug must contain only lowercase letters, numbers, and hyphens",
      });
      return;
    }

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "You must be logged in to verify slug availability",
        life: 5000,
      });
      return;
    }

    this.verifying.set(true);
    this.slugVerificationResult.set(null);

    this.quickLinksService
      .slugifyText(this.siteId(), slug, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.slugVerificationResult.set({
            verified: true,
            isAvailable: response.isAvailable,
            message: response.message || "",
          });
          this.verifying.set(false);

          // Show toast notification
          if (response.isAvailable) {
            this.messageService.add({
              severity: "success",
              summary: "Slug Available",
              detail: `The slug "${slug}" is available!`,
              life: 3000,
            });
          } else {
            this.messageService.add({
              severity: "error",
              summary: "Slug Not Available",
              detail: response.message || `The slug "${slug}" is already taken`,
              life: 5000,
            });
          }
        },
        error: () => {
          this.verifying.set(false);
          this.slugVerificationResult.set(null);
        },
      });
  }

  /**
   * Clear slug verification result when slug changes
   */
  onSlugChange(): void {
    this.slugVerificationResult.set(null);
  }

  /**
   * Computed property for template URL (for unlimited/limited use links)
   */
  readonly templateUrl = computed(() => {
    const usageLimitType = this.quickLinkForm.controls.usageLimitType.value;

    // Single-use links use token-based URLs, not template URLs
    // UsageLimitType: 0 = SingleUse, 1 = LimitedUse, 2 = Unlimited
    if (usageLimitType === 0) {
      return null;
    }

    // In edit mode, use templateUrl from API response if available
    const quickLink = this.quickLink();
    if (quickLink?.templateUrl) {
      return quickLink.templateUrl;
    }

    // In create mode, construct placeholder template URL
    const shopDomain = "your-store.myshopify.com";
    const slug = this.quickLinkForm.controls.slug.value || "{slug}";
    return `https://${shopDomain}/a/autoship/l/${slug}/{scheduled_order_id}`;
  });

  /**
   * Computed property for example URL with sample SO ID
   */
  readonly exampleUrl = computed(() => {
    const templateUrl = this.templateUrl();
    if (!templateUrl) return null;
    const previewId = this.previewScheduledOrderId() || "12345";
    return templateUrl.replace("{scheduled_order_id}", previewId);
  });

  /**
   * Computed property for custom redirect URL preview with UTM parameters
   * Shows real-time preview of the final URL that customers will be redirected to
   */
  readonly customUrlPreview = computed(() => {
    // Reference the tracker to make this computed reactive to form changes
    this.formChangeTracker();

    // Only show preview for custom URL redirect type
    if (
      !this.quickLinkForm ||
      this.quickLinkForm.controls.redirectType.value !== 2
    ) {
      return null;
    }

    const baseUrl = this.quickLinkForm.controls.redirectUrl.value;
    if (!baseUrl || baseUrl.trim() === "") {
      return null;
    }

    // Build UTM parameters
    const utmParams: string[] = [];
    const utmSource = this.quickLinkForm.controls.utmSource.value;
    const utmMedium = this.quickLinkForm.controls.utmMedium.value;
    const utmCampaign = this.quickLinkForm.controls.utmCampaign.value;
    const utmContent = this.quickLinkForm.controls.utmContent.value;

    if (utmSource)
      utmParams.push(`utm_source=${encodeURIComponent(utmSource)}`);
    if (utmMedium)
      utmParams.push(`utm_medium=${encodeURIComponent(utmMedium)}`);
    if (utmCampaign)
      utmParams.push(`utm_campaign=${encodeURIComponent(utmCampaign)}`);
    if (utmContent)
      utmParams.push(`utm_content=${encodeURIComponent(utmContent)}`);

    // If no UTM parameters, return just the base URL
    if (utmParams.length === 0) {
      return baseUrl;
    }

    // Append UTM parameters to base URL
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${utmParams.join("&")}`;
  });

  /**
   * Check if the quick link is deleted (status === 2)
   */
  readonly isDeleted = computed(() => {
    const quickLink = this.quickLink();
    return quickLink?.status === 2;
  });

  /**
   * Get the current status of the quick link
   */
  readonly currentStatus = computed(() => {
    return this.quickLink()?.status ?? null;
  });

  /**
   * Get status label for display
   */
  getStatusLabel(status: QuickLinkStatus | null): string {
    if (status === null) return "Unknown";
    const labels: Record<number, string> = {
      0: "Disabled",
      1: "Enabled",
      2: "Deleted",
    };
    return labels[status] || "Unknown";
  }

  /**
   * Get status severity for p-tag
   */
  getStatusSeverity(
    status: QuickLinkStatus | null,
  ): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" {
    if (status === null) return "secondary";
    switch (status) {
      case 0: // Disabled
        return "secondary";
      case 1: // Enabled
        return "success";
      case 2: // Deleted
        return "danger";
      default:
        return "secondary";
    }
  }

  /**
   * Handle permanent delete for deleted quick links
   * Shows confirmation dialog before proceeding
   */
  onPermanentDelete(): void {
    const quickLinkId = this.quickLinkId();
    if (!quickLinkId) return;

    const quickLinkName = this.quickLink()?.name || "this quick link";

    this.confirmationService.confirm({
      message: `Are you sure you want to permanently delete "<strong>${quickLinkName}</strong>"? This action cannot be undone. All metrics, consumed links, and tracking data will be permanently lost.`,
      header: "Permanent Delete",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Yes, Delete Permanently",
      rejectLabel: "Cancel",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        this.executePermanentDelete(quickLinkId);
      },
    });
  }

  /**
   * Execute the permanent delete after confirmation
   */
  private executePermanentDelete(quickLinkId: number): void {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "You must be logged in to delete quick links",
        life: 5000,
      });
      return;
    }

    this.saving.set(true);

    this.quickLinksService
      .permanentDeleteQuickLink(this.siteId(), quickLinkId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Permanently Deleted",
            detail: "The quick link has been permanently deleted",
            life: 5000,
          });
          this.navigateBack();
        },
        error: () => {
          this.saving.set(false);
        },
      });
  }

  /**
   * Copy URL to clipboard and show success toast
   */
  copyUrlToClipboard(url: string): void {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        this.messageService.add({
          severity: "success",
          summary: "Copied!",
          detail: "URL copied to clipboard",
          life: 2000,
        });
      })
      .catch(() => {
        this.messageService.add({
          severity: "error",
          summary: "Copy Failed",
          detail: "Failed to copy URL to clipboard",
          life: 3000,
        });
      });
  }
}
